import { useState, useRef, useEffect } from 'react';

const NAV_LINKS = [
  { href: '/', icon: 'ri-home-line', label: 'Home' },
  { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
  { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
  { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
  { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
  { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
];

export default function FanDuelPredictsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-l2l-strategy';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'L2L Strategy — Liability to Liquidity Sports Prediction Market',
      description: 'Explore the L2L strategy for migrating from traditional sportsbooks to CFTC-regulated prediction markets. Zero juice, peer-to-peer sports trading.',
      url: `${siteUrl}/l2l-strategy`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'L2L Strategy', item: `${siteUrl}/l2l-strategy` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>

            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>

              {/* Markets Dropdown */}
              <div className="relative" ref={marketsDropdownRef} onMouseLeave={() => setMarketsDropdownOpen(false)}>
                <button
                  onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Market Types</span>
                    </div>
                    {[
                      { label: 'Spreads',      icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400',    path: '/markets/spreads' },
                      { label: 'Money Lines',  icon: 'ri-copper-coin-line',       color: 'text-amber-400',   path: '/markets/money-lines' },
                      { label: 'Futures',      icon: 'ri-calendar-event-line',    color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays',      icon: 'ri-stack-line',             color: 'text-violet-400',  path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line',         color: 'text-cyan-400',    path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 transition-colors cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <a href="/why-rtj"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>Why L2L</div></a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
              <a href="/my-account">
                <div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700">
                  <i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i>
                </div>
              </a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {NAV_LINKS.map(link => (
                <a key={link.href} href={link.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${link.icon} text-base`}></i>
                    <span className="text-sm font-medium">{link.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Embed Section */}
      <section className="pt-6 pb-8 px-0">
        <div className="w-full">
          <div className="rounded-none border-0 overflow-hidden shadow-2xl" style={{ background: 'rgba(25,25,25,0.9)' }}>
            <div className="p-0">
              <div className="w-full" style={{ minHeight: '85vh' }}>
                <iframe
                  src="https://gamma.app/embed/4h4w0g3zbnx7ijr"
                  style={{ width: '100%', maxWidth: '100%', height: '85vh', border: 'none' }}
                  allow="fullscreen"
                  title="L2L-Strategy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4 mt-8" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="h-10 w-40 flex items-center justify-center">
            <img
              src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
              alt="L2L Logo"
              className="h-full w-full object-contain"
            />
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/markets" className="hover:text-slate-300 transition-colors cursor-pointer">Markets</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
